/**
 * Pure helpers for the narrow-viewport "not optimized for mobile" interstitial.
 *
 * The dense admin surfaces (record tables, advanced search, the Monaco flow
 * editor, dependency visualization, object/template canvas) are inherently
 * desktop-first. On a narrow viewport we gate them behind a friendly
 * interstitial instead of shipping a broken experience, while the login and the
 * rendered dashboard view ARE mobile-friendly and render normally.
 *
 * Kept as a separate, dependency-free module so the gating decision can be unit
 * tested without mounting the router.
 */

/** sessionStorage flag: the user chose "continue on mobile anyway" this session. */
export const MOBILE_INTERSTITIAL_DISMISS_KEY = "mobile-interstitial-dismissed";

/**
 * Signed-in routes that are mobile-friendly and must NOT be gated. Login and the
 * other signed-out routes render in a separate branch that never reaches the
 * gate, so only the rendered dashboard view (`/dashboard`, no route flag) is
 * allowlisted here. `/dashboard/new` and `/dashboard?edit` are the dense config
 * editor and stay gated.
 */
export const isMobileFriendlyPath = (pathname: string): boolean =>
    pathname === "/dashboard";

/**
 * Whether the narrow-viewport interstitial should replace the current screen.
 * True only when the viewport is narrow, the user has not dismissed it this
 * session, and the current path is not a mobile-friendly one.
 */
export const shouldShowMobileInterstitial = ({
    isNarrow,
    dismissed,
    pathname,
}: {
    isNarrow: boolean;
    dismissed: boolean;
    pathname: string;
}): boolean => isNarrow && !dismissed && !isMobileFriendlyPath(pathname);

/** Read the session dismissal flag (safe in non-browser contexts). */
export const isMobileInterstitialDismissed = (): boolean => {
    try {
        return (
            typeof window !== "undefined" &&
            window.sessionStorage?.getItem(MOBILE_INTERSTITIAL_DISMISS_KEY) === "1"
        );
    } catch {
        // sessionStorage can throw in private-mode / sandboxed contexts.
        return false;
    }
};

/** Persist the "continue on mobile anyway" choice for the session. */
export const dismissMobileInterstitial = (): void => {
    try {
        window.sessionStorage?.setItem(MOBILE_INTERSTITIAL_DISMISS_KEY, "1");
    } catch {
        // Best-effort: if storage is unavailable the choice simply won't persist.
    }
};
